import { Group } from '@mantine/core';
import type { SellerApplicationStatus } from '@modules/seller-applications/api/seller-applications-moderation.api';
import type {
  SellerAccountBlockDto,
  SellerProfileDeletionDto,
} from '@modules/sellers/api/sellers.api';
import '@modules/orders/components/status-tag.css';

const APPLICATION_STATUS_LABELS: Record<SellerApplicationStatus, string> = {
  DRAFT: 'Черновик',
  PENDING_MODERATION: 'На модерации',
  APPROVED: 'Одобрена',
  REJECTED: 'Отклонена',
};

const APPLICATION_STATUS_COLORS: Record<SellerApplicationStatus, { bg: string; color: string }> = {
  DRAFT: { bg: '#e5e7eb', color: '#374151' },
  PENDING_MODERATION: { bg: '#fef3c7', color: '#92400e' },
  APPROVED: { bg: '#86efac', color: '#166534' },
  REJECTED: { bg: '#fecaca', color: '#991b1b' },
};

function StatusTag({ label, bg, color }: { label: string; bg: string; color: string }) {
  return (
    <span className="status-tag" style={{ backgroundColor: bg, color }}>
      {label}
    </span>
  );
}

/** Одобренный магазин изменил название / описание / логотип — ждёт модерации. */
export function SellerProfileChangesTag() {
  return <StatusTag label="Изменения на модерации" bg="#fef3c7" color="#92400e" />;
}

type Props = {
  status: SellerApplicationStatus | null | undefined;
  accountBlock?: SellerAccountBlockDto | null;
  profileHidden?: boolean;
  profileDeletion?: SellerProfileDeletionDto | null;
  /** В списке — короткие подписи; в карточке — полные */
  compact?: boolean;
};

export function SellerAdminStatusBadge({
  status,
  accountBlock,
  profileHidden,
  profileDeletion,
  compact = false,
}: Props) {
  if (accountBlock != null) {
    return <StatusTag label="Заблокирован" bg="#fecaca" color="#991b1b" />;
  }

  if (profileDeletion?.deletedAt) {
    return <StatusTag label="Удалён" bg="#9ca3af" color="#1f2937" />;
  }

  if (profileDeletion && !profileDeletion.deletedAt) {
    return (
      <StatusTag
        label={compact ? 'Удаление запланировано' : 'Заявка на удаление'}
        bg="#ffedd5"
        color="#9a3412"
      />
    );
  }

  if (profileHidden) {
    return <StatusTag label="Скрыт с витрины" bg="#e5e7eb" color="#374151" />;
  }

  if (!status) return '—';
  const colors = APPLICATION_STATUS_COLORS[status];
  return <StatusTag label={APPLICATION_STATUS_LABELS[status]} bg={colors.bg} color={colors.color} />;
}

type SellerAdminStatusBadgesProps = Props & {
  showDeletionDate?: boolean;
};

/** Карточка продавца: основной бейдж + дата удаления при активной заявке */
export function SellerAdminStatusBadges({
  profileDeletion,
  showDeletionDate = false,
  ...badgeProps
}: SellerAdminStatusBadgesProps) {
  const scheduledLabel =
    showDeletionDate && profileDeletion?.scheduledAt && !profileDeletion.deletedAt
      ? new Date(profileDeletion.scheduledAt).toLocaleString('ru-RU', {
          dateStyle: 'long',
          timeStyle: 'short',
        })
      : null;

  return (
    <Group gap="xs" wrap="wrap">
      <SellerAdminStatusBadge {...badgeProps} profileDeletion={profileDeletion} />
      {scheduledLabel ? (
        <StatusTag label={`до ${scheduledLabel}`} bg="#ffedd5" color="#9a3412" />
      ) : null}
    </Group>
  );
}
